import React, { useState } from 'react';
import { Box, Button, TextField, Typography } from '@mui/material';
import { twoFactorService } from '../../services/twofactor-service';
import type { AuthResponse } from '../../types/api';

interface TwoFactorChallengeProps {
  /** Short-lived, single-purpose token from the password step. */
  challengeToken: string;
  /** Called with the real session once the second factor checks out. */
  onVerified: (auth: AuthResponse) => void;
  /** Abandon the attempt and go back to the password form. */
  onCancel: () => void;
}

/**
 * Second step of a gated sign-in.
 *
 * Rendered instead of — never alongside — the password form, because the
 * password step returned no tokens: there is no session yet, and the challenge
 * token is the only thing carrying the attempt forward.
 */
export const TwoFactorChallenge: React.FC<TwoFactorChallengeProps> = ({
  challengeToken,
  onVerified,
  onCancel,
}) => {
  const [code, setCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim()) {
      setError('Enter a code to continue');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      onVerified(await twoFactorService.verifyChallenge(challengeToken, code.trim()));
    } catch (err: any) {
      setError(err?.message || 'That code is not valid');
      setBusy(false);
    }
  };

  const switchMode = () => {
    setUseRecovery((v) => !v);
    setCode('');
    setError(null);
  };

  return (
    <Box>
      <Typography sx={{ fontWeight: 650, fontSize: '1.05rem', mb: 0.75 }}>
        Enter your code
      </Typography>
      <Typography sx={{ color: 'var(--text-secondary)', lineHeight: 1.7, mb: 3 }}>
        {useRecovery
          ? 'Enter one of the recovery codes you saved. Each one works once.'
          : 'Open your authenticator app and enter the six-digit code for FolioWorth.'}
      </Typography>

      {error && (
        <Typography role="alert" sx={{ color: 'var(--danger-text)', mb: 2 }}>
          {error}
        </Typography>
      )}

      <Box component="form" onSubmit={submit}>
        <TextField
          fullWidth
          autoFocus
          label={useRecovery ? 'Recovery code' : 'Verification code'}
          value={code}
          onChange={(e) => setCode(e.target.value)}
          disabled={busy}
          autoComplete="one-time-code"
          inputProps={{
            // Recovery codes are letters and dashes, so the numeric keypad
            // hint only applies to the TOTP case.
            inputMode: useRecovery ? 'text' : 'numeric',
            maxLength: useRecovery ? 20 : 7,
            'aria-label': useRecovery ? 'Recovery code' : 'Verification code',
          }}
        />

        <Button type="submit" variant="contained" fullWidth sx={{ mt: 3 }} disabled={busy}>
          {busy ? 'Checking…' : 'Continue'}
        </Button>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2.5 }}>
          <Box
            component="button"
            type="button"
            onClick={switchMode}
            disabled={busy}
            sx={{
              background: 'none', border: 'none', p: 0, font: 'inherit',
              fontSize: '0.875rem', cursor: 'pointer', color: 'var(--text-faint)',
              borderBottom: '1px solid transparent',
              '&:hover': { color: 'var(--text)', borderBottomColor: 'var(--text)' },
            }}
          >
            {useRecovery ? 'Use my authenticator app' : 'Use a recovery code instead'}
          </Box>
          <Box
            component="button"
            type="button"
            onClick={onCancel}
            disabled={busy}
            sx={{
              background: 'none', border: 'none', p: 0, font: 'inherit',
              fontSize: '0.875rem', cursor: 'pointer', color: 'var(--text-faint)',
              borderBottom: '1px solid transparent',
              '&:hover': { color: 'var(--text)', borderBottomColor: 'var(--text)' },
            }}
          >
            Back
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
